<template>
  <div>
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">emoji_events</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Reward History</h2>
          <p class="text-sm text-gray-500">Track rewards issued to users.</p>
        </div>
      </div>
      <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">filter_list</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Filters</span>
          </div>
          <export-button
            :downloadUrl="reportDownloadUrl"
            :working="loading"
          ></export-button>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- Status -->
          <div class="w-full sm:w-48">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Status</label>
            <select
              class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
              v-model="selectedFilter"
            >
              <option value="all" v-if="!financeRole">All</option>
              <option
                v-for="status in statuses"
                :key="`status-${status}`"
                :value="status"
              >
                {{ status }}
              </option>
            </select>
          </div>

          <!-- Passenger -->
          <div class="relative w-full sm:w-56">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Passenger</label>
            <div class="relative mt-1.5">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="text-base text-gray-500 material-icons-round">search</span>
              </span>
              <input
                v-model="selectedPassenger"
                @keyup="searchPassenger"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search passenger..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="passengerSearchResults.length"
                class="absolute inset-x-0 z-50 mt-2"
              >
                <div
                  class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                >
                  <ul>
                    <li
                      v-for="(result, index) in passengerSearchResults"
                      :key="result._id"
                      @click="selectPassenger(index)"
                      class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    >
                      <p class="text-sm font-semibold text-gray-800">
                        {{ `${result.firstName} ${result.lastName}` }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ result.phoneNumber }}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>

          <!-- Date range -->
          <div class="w-full sm:w-auto">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Date Range</label>
            <div class="flex items-center h-10 mt-1.5 bg-white border border-gray-300 shadow-sm rounded-xl">
              <span class="pl-3 pr-1 text-base text-gray-500 material-icons-round">event</span>
              <v-date-picker v-model="range" is-range>
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex items-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      placeholder="From"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                    <span class="mx-1 text-base text-gray-300 material-icons-round">arrow_forward</span>
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      placeholder="To"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                  </div>
                </template>
              </v-date-picker>
              <button
                v-if="range.start || range.end"
                @click="range = { start: null, end: null }"
                type="button"
                title="Clear dates"
                class="flex items-center justify-center w-8 h-8 mx-1 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
              >
                <span class="text-lg material-icons-round">close</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4 overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-for="col in ['Passenger', 'Prize', 'Status', 'Date/Time', '']"
                  :key="`col-${col}`"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody class="relative" v-if="rewards.length">
              <tr
                v-for="(reward, index) in rewards"
                :key="reward._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <!-- Passenger -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div v-if="reward.passenger" class="flex items-center space-x-3">
                    <span
                      class="flex items-center justify-center flex-shrink-0 w-10 h-10 text-xs font-bold rounded-full"
                      :class="avatarClass(personLabel(reward.passenger))"
                    >{{ initials(personLabel(reward.passenger)) }}</span>
                    <div>
                      <p class="font-medium text-gray-900">
                        {{ personLabel(reward.passenger, "Unknown") }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ reward.passenger.phoneNumber || "—" }}
                      </p>
                    </div>
                  </div>
                  <span v-else class="text-gray-400">Unknown</span>
                </td>
                <!-- Prize -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-2">
                    <i class="text-base text-taxiye-500 material-icons-round">card_giftcard</i>
                    <span class="font-medium text-gray-900">
                      {{ reward.prize ? reward.prize.prizeItem : "—" }}
                    </span>
                  </div>
                </td>
                <!-- Status -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <span
                    class="inline-flex items-center px-2 py-1 text-xs font-semibold leading-none rounded-full"
                    :class="statusBadgeClass(reward.status)"
                  >
                    <span
                      class="w-2 h-2 mr-2 rounded-full"
                      :class="statusDotClass(reward.status)"
                    ></span>
                    {{ reward.status }}
                  </span>
                </td>
                <!-- Date/Time -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  <div class="flex items-center space-x-2">
                    <i class="text-sm text-gray-400 material-icons-round">schedule</i>
                    <span>{{ formatDate(reward.createdAt) }}</span>
                  </div>
                </td>
                <!-- Actions -->
                <td
                  class="px-5 py-4 text-right whitespace-no-wrap"
                  v-click-outside="hideDropdown"
                >
                  <div
                    @click.stop
                    class="relative inline-block text-left"
                    v-if="reward.status === 'ISSUED' && !financeRole"
                  >
                    <button
                      @click="toggleDropdown(index)"
                      type="button"
                      class="inline-flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
                      aria-haspopup="true"
                      :aria-expanded="openDropdown === index"
                    >
                      <i class="material-icons-round">more_vert</i>
                    </button>
                    <transition
                      enter-active-class="transition duration-100 ease-out"
                      enter-class="transform scale-95 opacity-0"
                      enter-to-class="transform scale-100 opacity-100"
                      leave-active-class="transition duration-75 ease-in"
                      leave-class="transform scale-100 opacity-100"
                      leave-to-class="transform scale-95 opacity-0"
                    >
                      <div
                        v-if="openDropdown === index"
                        class="absolute right-0 z-10 w-56 mt-1 origin-top-right bg-white border border-gray-100 shadow-lg rounded-xl"
                      >
                        <div
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="options-menu"
                          class="py-1"
                        >
                          <button
                            @click="changeStatus(reward._id, 'DELIVERED')"
                            class="flex items-center w-full px-4 py-2 space-x-2 text-sm text-left text-gray-700 transition-colors duration-150 hover:bg-taxiye-50 focus:outline-none"
                            role="menuitem"
                          >
                            <i class="text-base text-green-500 material-icons-round">check_circle</i>
                            <span>Mark Delivered</span>
                          </button>
                        </div>
                      </div>
                    </transition>
                  </div>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="5" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100">
                      <i class="text-3xl material-icons-round">inbox</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500">No rewards found</span>
                    <span class="mt-1 text-xs text-gray-400">Try adjusting your filters</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
// import moment from "moment";
import PaginationMixin from "@/mixins/Pagination";
import ExportButton from "@/components/ExportButton";
import Pagination from "@/components/Pagination";
// import { ROLES } from "../../config";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      rewards: [],
      openDropdown: false,
      selectedAccount: "",
      loading: true,
      accounts: [],
      accountId: null,
      selectedFilter: "all",

      selectedDriver: "",
      selectedPassenger: "",

      driverSearchResults: [],
      userSearchResults: [],
      passengerSearchResults: [],

      driverId: null,

      userId: null,

      passengerId: null,

      range: {
        start: null,
        end: null,
      },

      statuses: [
        "ISSUED",
        "DELIVERED"
      ]
    };
  },
  components: {
    ExportButton,
    Pagination,
  },
  watch: {
    currentPage() {
      this.getRewards();
    },
    perPage() {
      this.currentPage === 1 ? this.getRewards() : (this.currentPage = 1);
    },
    selectedFilter() {
      this.getRewards();
    },
    range: {
      handler() {
        this.getRewards();
      },
      deep: true,
    },
    $route() {
      if (this.$route.query.status) {
        this.selectedFilter = this.$route.query.status;
      }
    },
  },
  created() {
    if (this.financeRole) {
      this.selectedFilter = "DELIVERED"
      this.statuses = [
        "DELIVERED"
      ]
    } else {
      this.statuses = [
        "ISSUED",
        "DELIVERED"
      ]
    }

    if (this.$route.query.status) {
      this.selectedFilter = this.$route.query.status;
    }
    if (this.$route.query.page) {
      this.currentPage = this.$route.query.page;
    }
    if (this.$route.query.passenger) {
      this.passengerId = this.$route.query.passenger;
      this.$misc
        .get("users/" + this.$route.query.passenger)
        .then(({ data: { firstName, lastName, phoneNumber } }) => {
          this.selectedPassenger = `${firstName} ${lastName} - ${phoneNumber}`;
        });
    }
    if (this.$route.query.driver) {
      this.driverId = this.$route.query.driver;
      this.$misc
        .get("drivers/" + this.$route.query.driver)
        .then(({ data }) => {
          const { firstName, lastName, phoneNumber } = data.driver
          this.selectedDriver = `${firstName} ${lastName} - ${phoneNumber}`;
          this.selectedDriverObj = data.driver
        });
    }

    this.getRewards();
    this.$misc
      .get("/accounts")
      .then((response) => (this.accounts = response.data.data))
      .catch((err) => console.log(err));
  },
  computed: {
    driverFilter() {
      return this.$route.query.driver || null;
    },
    // financeRole() {
    //   return this.$store.getters.role === ROLES.FINANCE
    // },
    riderFilter() {
      return this.$route.query.passenger || null;
    },
    reportDownloadUrl() {
      return (
        "rewards/export?" +
        Object.entries({
          ...Object.fromEntries(
            Object.entries({
              driver: this.driverId ? this.driverId : undefined,
              dispatcher: this.userId ? this.userId : undefined,
              passenger: this.passengerId ? this.passengerId : undefined,
              status:
                this.selectedFilter && this.selectedFilter != "all"
                  ? this.selectedFilter
                  : undefined,
              ...(this.range || {}),
            }).filter(([key, val]) => key && val)
          ),
        })
          .map(([key, value]) => [key, value].join("="))
          .join("&")
      );
    },
  },
  methods: {
    searchDriver(e) {
      if (e.target.value.trim() === "") {
        this.driverSearchResults = [];
        this.driverId = null;
        this.getRewards();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.driverSearchResults = [];
          this.$misc
            .get("drivers/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.driverSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.driverSearchResults = [];
        }
      }
    },

    changeStatus(id, status) {
      this.$misc
            .post(`rewards/${id}/change-status`, {
              status: status
            })
            .then(() => {
              window.flash({
                type: "success",
                body: `Reward marked ${status} successfully!`,
              });
              this.getRewards()
            })
            .catch((err) => {
              window.flash({
                type: "error",
                body: `Error while trying to modify the status!`,
              });
              console.log(err)
            })
            .finally(() => (this.loading = false));
    },

    searchUser(e) {
      if (e.target.value.trim() === "") {
        this.userSearchResults = [];
        this.userId = null;
        this.getRewards();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.userSearchResults = [];
          this.$misc
            .get("accounts/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.userSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.userSearchResults = [];
        }
      }
    },

    searchPassenger(e) {
      if (e.target.value.trim() === "") {
        this.passengerSearchResults = [];
        this.passengerId = null;
        this.getRewards();
      } else {
        const name = e.target.value.trim();

        if (name) {
          this.passengerSearchResults = [];
          this.$misc
            .get("users/search?vehicle=true&limit=5&q=" + name)
            .then(({ data }) => {
              this.passengerSearchResults = data;
            })
            .catch((err) => console.log(err))
            .finally(() => (this.loading = false));
        } else {
          this.passengerSearchResults = [];
        }
      }
    },

    selectDriver(index) {
      let driver = this.driverSearchResults[index];
      this.selectedDriverObj = driver;
      this.selectedDriver = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
      this.driverId = driver._id;
      this.driverSearchResults = [];
      const query = { ...this.$route.query, driver: this.driverId };
      this.$router.push({ query });
      this.getRewards();
    },

    selectUser(index) {
      let user = this.userSearchResults[index];
      this.selectedUserObj = user;
      this.selectedUser = `${user.firstName} ${user.lastName}`;
      this.userId = user._id;
      this.userSearchResults = [];
      this.getRewards();
    },

    selectPassenger(index) {
      let user = this.passengerSearchResults[index];
      this.selectedPassengerObj = user;
      this.selectedPassenger = `${user.firstName} ${user.lastName} - ${user.phoneNumber}`;
      this.passengerId = user._id;
      this.passengerSearchResults = [];
      const query = { ...this.$route.query, passenger: this.passengerId };
      this.$router.push({ query });

      this.getRewards();
    },

    formatDate(date) {
      // return moment(date).format("MMM D, YYYY HH:mm");
      return date ? date.replace("T", " ").slice(0, 16) : "";
    },
    personLabel(person, fallback = "") {
      if (!person) return fallback;
      const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
      if (!name) return fallback;
      return name
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    },
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    avatarClass(name) {
      const palette = [
        "bg-taxiye-100 text-taxiye-600",
        "bg-indigo-50 text-indigo-600",
        "bg-blue-50 text-blue-600",
        "bg-green-50 text-green-600",
        "bg-purple-50 text-purple-600",
        "bg-pink-50 text-pink-600",
      ];
      let hash = 0;
      for (let i = 0; i < (name || "").length; i++) {
        hash = (hash + name.charCodeAt(i)) % palette.length;
      }
      return palette[hash];
    },
    statusBadgeClass(status) {
      return (
        {
          ISSUED: "text-yellow-700 bg-yellow-100",
          DELIVERED: "text-green-700 bg-green-100",
        }[status] || "text-gray-600 bg-gray-100"
      );
    },
    statusDotClass(status) {
      return (
        {
          ISSUED: "bg-yellow-500",
          DELIVERED: "bg-green-500",
        }[status] || "bg-gray-400"
      );
    },
    toggleDropdown(index) {
      if (this.openDropdown === index) {
        this.openDropdown = false;
      } else {
        this.openDropdown = index;
      }
    },
    hideDropdown() {
      this.openDropdown = false;
    },
    getRewards() {
      this.loading = true;
      var path = `rewards?limit=${this.perPage}&page=${this.currentPage}&populate=["driver","passenger","vehicleType","dispatcher"]`;

      if (this.driverId) {
        path += "&driver=" + this.driverId;
      }

      if (this.passengerId) {
        path += "&passenger=" + this.passengerId;
      }

      if (this.userId) {
        path += "&dispatcher=" + this.userId;
      }

      if (this.selectedFilter && this.selectedFilter != "all") {
        path += "&status=" + this.selectedFilter;
      }

      if (this.range && this.range.start && this.range.end) {
        path += "&from=" + this.range.start + "&to=" + this.range.end;
      }

      this.$misc
        .get(path)
        .then((response) => {
          this.rewards = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
  },
};
</script>
